import React from 'react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { BreadTransferRow, UpdateTransferRowPayload, TransferShift } from '../types/bread-transfers.types';

interface TransferTableProps {
  locations: BreadTransferRow[];
  totals: {
    parameterPreviousDay: number;
    initialStock: number;
    waste: number;
    previousDayDispatch: number;
    fixedDispatch: number;
    calculatedDispatch: number;
  };
  shift: TransferShift;
  onUpdateRow: (payload: UpdateTransferRowPayload) => void;
  disabled?: boolean;
}

export const TransferTable: React.FC<TransferTableProps> = ({
  locations,
  totals,
  shift,
  onUpdateRow,
  disabled = false
}) => {
  const handleInputChange = (
    locationId: string,
    field: keyof Omit<BreadTransferRow, 'locationId' | 'locationCode' | 'locationName' | 'calculatedDispatch'>,
    value: string
  ) => {
    const numValue = parseInt(value) || 0;
    onUpdateRow({
      locationId,
      field,
      value: numValue
    });
  };

  return (
    <Card>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LOCAL</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-yellow-50">
                    PARÁMETRO DÍA ANTERIOR
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    STOCK INICIAL
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DESCARTE
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-yellow-50">
                    Q' DESPACHAR DÍA ANTERIOR
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-yellow-50">
                    DESPACHO FIJO {shift === 'morning' ? 'MAÑANA' : 'TARDE'}
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                    CANTIDAD A DESPACHAR
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {locations.map((location) => (
                  <tr key={location.locationId} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-sm text-gray-900">
                        {location.locationCode} ({location.locationName})
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center bg-yellow-50">
                      <div className="flex justify-center">
                        <Input
                          type="number"
                          value={location.parameterPreviousDay}
                          onChange={(e) => handleInputChange(location.locationId, 'parameterPreviousDay', e.target.value)}
                          className="w-20 text-center font-semibold text-sm h-8"
                          min="0"
                          disabled={disabled}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold text-sm text-gray-900">
                        {location.initialStock}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold text-sm text-gray-900">
                        {location.waste}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center bg-yellow-50">
                      <div className="flex justify-center">
                        <Input
                          type="number"
                          value={location.previousDayDispatch}
                          onChange={(e) => handleInputChange(location.locationId, 'previousDayDispatch', e.target.value)}
                          className="w-20 text-center font-semibold text-sm h-8"
                          min="0"
                          disabled={disabled}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center bg-yellow-50">
                      <div className="flex justify-center">
                        <Input
                          type="number"
                          value={location.fixedDispatch}
                          onChange={(e) => handleInputChange(location.locationId, 'fixedDispatch', e.target.value)}
                          className="w-20 text-center font-semibold text-sm h-8"
                          min="0"
                          disabled={disabled}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center bg-blue-50">
                      <div className="font-bold text-blue-700 text-lg">
                        {location.calculatedDispatch}
                      </div>
                    </td>
                  </tr>
                ))}
                
                {/* Totals Row */}
                <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold">
                  <td className="px-3 py-2 text-gray-900">TOTALES</td>
                  <td className="px-3 py-2 text-center text-gray-900 bg-yellow-100">{totals.parameterPreviousDay}</td>
                  <td className="px-3 py-2 text-center text-gray-900">{totals.initialStock}</td>
                  <td className="px-3 py-2 text-center text-gray-900">{totals.waste}</td>
                  <td className="px-3 py-2 text-center text-gray-900 bg-yellow-100">{totals.previousDayDispatch}</td>
                  <td className="px-3 py-2 text-center text-gray-900 bg-yellow-100">{totals.fixedDispatch}</td>
                  <td className="px-3 py-2 text-center bg-blue-100 text-blue-800 text-lg">
                    {totals.calculatedDispatch}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};